import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createKBArticle } from "@/lib/firestore";
import { parseKbMarkdown, ParsedKbFile } from "@/lib/kbImport";
import { ALL_PLATFORMS, CATEGORIES, PLATFORM_LABELS } from "@/types";
import { Spinner, toast } from "@/components/shared";

interface ParsedRow extends ParsedKbFile {
  include: boolean;
  status: "pending" | "uploading" | "imported" | "error";
  error?: string;
  newId?: string;
}

export default function KbImport() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [importing, setImporting] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const handleFiles = async (files: FileList | File[]) => {
    const arr = Array.from(files).filter((f) =>
      /\.(md|markdown|txt)$/i.test(f.name) || f.type === "text/markdown" || f.type === "text/plain"
    );
    if (arr.length === 0) {
      toast("No .md / .markdown / .txt files found", "error");
      return;
    }
    const parsed: ParsedRow[] = [];
    for (const f of arr) {
      const text = await f.text();
      const result = parseKbMarkdown(text, f.name);
      parsed.push({ ...result, include: true, status: "pending" });
    }
    setRows(parsed);
  };

  const updateRow = (i: number, patch: Partial<ParsedRow>) =>
    setRows((rs) => rs.map((r, j) => j === i ? { ...r, ...patch } : r));

  const importAll = async () => {
    setImporting(true);
    let imported = 0;
    let failed = 0;
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.include || r.status === "imported") continue;
      updateRow(i, { status: "uploading", error: undefined });
      try {
        const id = await createKBArticle({
          title: r.title.trim(),
          body: r.body.trim(),
          category: r.category,
          platforms: r.platforms,
        });
        updateRow(i, { status: "imported", newId: id });
        imported++;
      } catch (err) {
        updateRow(i, { status: "error", error: (err as Error).message });
        failed++;
      }
    }
    setImporting(false);
    if (failed > 0) {
      toast(`Imported ${imported} · ${failed} failed`, imported > 0 ? "info" : "error");
    } else {
      toast(`Imported ${imported} article${imported === 1 ? "" : "s"}`);
    }
  };

  const allDone = rows.length > 0 && rows.every((r) => r.status === "imported" || !r.include);

  return (
    <div style={{ overflowY: "auto", flex: 1, padding: 20 }}>
      <button className="btn btn-secondary btn-sm" onClick={() => navigate("/kb")} style={{ marginBottom: 16 }}>← Back to Knowledge Base</button>
      <h1 style={{ marginBottom: 4 }}>Import Articles from Markdown</h1>
      <p style={{ color: "var(--text2)", fontSize: 13, marginBottom: 18, maxWidth: 720 }}>
        Drop one or more <code>.md</code> files. The importer reads <strong>YAML frontmatter</strong> first
        (<code>title:</code>, <code>category:</code>), then plain <code>Key: value</code> lines at the top, then falls back
        to the first <code>#</code> heading and the filename. Edit anything before importing.
      </p>

      <div
        onClick={() => fileInput.current?.click()}
        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        style={{
          border: "2px dashed var(--border-med)", borderRadius: "var(--rs)",
          padding: 22, textAlign: "center", cursor: "pointer", background: "var(--gray)",
          marginBottom: 18, maxWidth: 720,
        }}
      >
        <input ref={fileInput} type="file" multiple accept=".md,.markdown,.txt,text/markdown,text/plain"
          style={{ display: "none" }}
          onChange={(e) => { if (e.target.files) handleFiles(e.target.files); e.target.value = ""; }} />
        <div style={{ fontSize: 28, marginBottom: 8 }}>📄</div>
        <div style={{ fontSize: 14, fontWeight: 500 }}>Drop .md files here or click to browse</div>
        <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 4 }}>Multi-select supported</div>
      </div>

      {rows.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 13, color: "var(--text2)" }}>
              {rows.filter((r) => r.include).length} of {rows.length} files selected for import
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setRows([])} disabled={importing}>Clear</button>
              <button className="btn btn-primary btn-sm" onClick={importAll}
                disabled={importing || allDone || !rows.some((r) => r.include && r.status !== "imported")}>
                {importing ? <Spinner size={12} color="white" /> : null} Import {rows.filter((r) => r.include && r.status !== "imported").length}
              </button>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {rows.map((r, i) => (
              <div key={i} className="card card-body" style={{ opacity: r.include ? 1 : 0.5 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <input type="checkbox" checked={r.include}
                    onChange={(e) => updateRow(i, { include: e.target.checked })}
                    disabled={r.status === "uploading" || r.status === "imported"} />
                  <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text2)", flex: 1 }}>
                    {r.filename}
                  </span>
                  <StatusBadge status={r.status} error={r.error} />
                  {r.newId && (
                    <button className="btn btn-secondary btn-sm" onClick={() => navigate(`/kb/${r.newId}`)}>View →</button>
                  )}
                </div>
                <div className="frow" style={{ marginBottom: 8 }}>
                  <div className="fg">
                    <label>Title</label>
                    <input className="fi" value={r.title} onChange={(e) => updateRow(i, { title: e.target.value })}
                      disabled={r.status === "uploading" || r.status === "imported"} />
                  </div>
                  <div className="fg">
                    <label>Category</label>
                    <select className="fsel" value={r.category}
                      onChange={(e) => updateRow(i, { category: e.target.value })}
                      disabled={r.status === "uploading" || r.status === "imported"}>
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div className="fg" style={{ marginBottom: 8 }}>
                  <label>Platforms <span style={{ color: "var(--text2)", fontWeight: 400 }}>— leave empty for "applies to all"</span></label>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {ALL_PLATFORMS.map((p) => {
                      const on = r.platforms.includes(p);
                      const disabled = r.status === "uploading" || r.status === "imported";
                      return (
                        <button key={p} type="button" disabled={disabled}
                          onClick={() => updateRow(i, { platforms: on ? r.platforms.filter((x) => x !== p) : [...r.platforms, p] })}
                          style={{
                            fontSize: 11, padding: "4px 10px", borderRadius: 14,
                            border: `1px solid ${on ? "var(--blue)" : "var(--border-med)"}`,
                            background: on ? "var(--blue)" : "var(--surface)",
                            color: on ? "white" : "var(--text2)",
                            cursor: disabled ? "default" : "pointer", fontFamily: "var(--font)",
                          }}>
                          {PLATFORM_LABELS[p]}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <details style={{ fontSize: 12 }}>
                  <summary style={{ cursor: "pointer", color: "var(--text2)" }}>
                    Body preview ({r.body.length.toLocaleString()} chars)
                  </summary>
                  <pre style={{ background: "var(--gray)", padding: 10, borderRadius: 6, marginTop: 8,
                    fontSize: 11, lineHeight: 1.5, maxHeight: 220, overflow: "auto", whiteSpace: "pre-wrap" }}>
                    {r.body.slice(0, 2000)}{r.body.length > 2000 ? "\n…" : ""}
                  </pre>
                </details>
                {r.warnings.length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 11, color: "var(--orange-t)" }}>
                    ⚠ {r.warnings.join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function StatusBadge({ status, error }: { status: ParsedRow["status"]; error?: string }) {
  if (status === "pending") return <span className="badge" style={{ background: "var(--gray)", color: "var(--text2)" }}>ready</span>;
  if (status === "uploading") return <span className="badge" style={{ background: "var(--blue-l)", color: "var(--blue-t)" }}>importing…</span>;
  if (status === "imported") return <span className="badge" style={{ background: "var(--green-l)", color: "var(--green-t)" }}>✓ imported</span>;
  return <span className="badge" style={{ background: "var(--red-l)", color: "var(--red-t)" }} title={error}>error</span>;
}
